import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Warning, Shield, CaretRight, CaretDown, Check, X,
  ArrowRight, Upload, Robot, FileText, Trash,
  Eye, Clock, CheckCircle, XCircle, ArrowClockwise,
  FunnelSimple, MagnifyingGlass, Buildings, ListChecks,
  Target, Sparkle, ThumbsUp, PencilSimple, Link as LinkIcon,
  ClipboardText, ChartLineUp, Database, Bank, Globe, Briefcase, FirstAid, Users
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Link, useSearchParams } from 'react-router-dom';

// Regulatory theme definitions
const REGULATORY_THEMES = {
  cybersecurity: { name: 'Cybersecurity', icon: Shield, color: '#3B82F6' },
  data_protection: { name: 'Data Protection', icon: Database, color: '#10B981' },
  financial_crime: { name: 'Financial Crime', icon: Bank, color: '#F59E0B' },
  tax_compliance: { name: 'Tax Compliance', icon: Globe, color: '#8B5CF6' },
  corporate_governance: { name: 'Corporate Governance', icon: Briefcase, color: '#EC4899' },
  consumer_protection: { name: 'Consumer Protection', icon: FirstAid, color: '#14B8A6' },
  employment_labour: { name: 'Employment', icon: Users, color: '#F97316' }
};

// Status styles
const STATUS_STYLES = {
  auto_flagged: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'Auto-Flagged', icon: Warning },
  evidence_required: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Evidence Required', icon: Upload },
  pending_validation: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', label: 'Pending Validation', icon: Robot },
  evidence_failed: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'Evidence Failed', icon: XCircle },
  pending_review: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', label: 'Pending Review', icon: Eye },
  additional_evidence_requested: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', label: 'Additional Evidence Requested', icon: ArrowClockwise },
  accepted: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Accepted', icon: CheckCircle },
  rejected: { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400', label: 'Rejected', icon: ArrowRight }
};

// NAVI Result styles
const NAVI_RESULT_STYLES = {
  MEETS: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: '✅', label: 'Meets Requirements' },
  PARTIALLY_MEETS: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: '⚠️', label: 'Partially Meets' },
  DOES_NOT_MEET: { bg: 'bg-red-500/10', text: 'text-red-400', icon: '❌', label: 'Does Not Meet' }
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color = 'gold', subtext }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-background-surface border border-border rounded-lg p-4"
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-text-tertiary uppercase tracking-wider">{label}</span>
      {Icon && <Icon size={18} className={`text-${color}`} />}
    </div>
    <div className={`text-2xl font-mono font-bold text-${color}`}>{value}</div>
    {subtext && <div className="text-xs text-text-tertiary mt-1">{subtext}</div>}
  </motion.div>
);

// Risk Log Item Card
const RiskLogItemCard = ({ item, onAction, onExpand, isExpanded }) => {
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);
  
  const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.auto_flagged;
  const StatusIcon = statusStyle.icon;
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/risk-log/items/${item.id}/upload-evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Evidence uploaded');
        onAction('refresh');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleAnalyze = async (evidenceId) => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/risk-log/items/${item.id}/analyze-evidence/${evidenceId}`);
      if (response.data.success) {
        toast.success(response.data.navi_result === 'MEETS' ? 'Evidence validated!' : 'Analysis complete');
        onAction('refresh');
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const handleMoveToReview = async () => {
    try {
      await axios.post(`${API}/risk-log/items/${item.id}/move-to-review`);
      toast.success('Moved to review');
      onAction('refresh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to review');
    }
  };
  
  const handleAccept = async () => {
    try {
      const formData = new FormData();
      await axios.post(`${API}/risk-log/items/${item.id}/accept`, formData);
      toast.success('Item accepted and closed');
      onAction('refresh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };
  
  const handleRequestEvidence = async () => {
    if (!notes.trim()) {
      toast.error('Please provide notes for the evidence request');
      return;
    }
    
    const formData = new FormData();
    formData.append('notes', notes);
    
    try {
      await axios.post(`${API}/risk-log/items/${item.id}/request-additional-evidence`, formData);
      toast.success('Additional evidence requested');
      setShowRequestModal(false);
      setNotes('');
      onAction('refresh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request evidence');
    }
  };
  
  const handleReject = async () => {
    if (!notes.trim()) {
      toast.error('Please provide rejection notes');
      return;
    }
    
    const formData = new FormData();
    formData.append('notes', notes);
    
    try {
      const response = await axios.post(`${API}/risk-log/items/${item.id}/reject`, formData);
      toast.success('Item rejected and moved to Remediation Tracker');
      setShowRejectModal(false);
      setNotes('');
      onAction('refresh');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };
  
  const canUploadEvidence = ['evidence_required', 'additional_evidence_requested', 'evidence_failed'].includes(item.status);
  const canMoveToReview = ['auto_flagged', 'evidence_failed'].includes(item.status);
  const canMakeDecision = ['pending_review', 'auto_flagged', 'evidence_failed'].includes(item.status);
  const isTerminal = ['accepted', 'rejected'].includes(item.status);
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-background-surface border rounded-lg overflow-hidden ${statusStyle.border}`}
      >
        {/* Header */}
        <button
          onClick={() => onExpand(item.id)}
          className={`w-full p-4 ${statusStyle.bg} flex items-center justify-between hover:opacity-90 transition-opacity`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-background/50`}>
              <span className={`text-sm font-mono font-bold ${statusStyle.text}`}>
                {item.score}
              </span>
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-text-primary">{item.question_id}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${statusStyle.bg} ${statusStyle.text} flex items-center gap-1`}>
                  <StatusIcon size={10} />
                  {statusStyle.label}
                </span>
              </div>
              <p className="text-xs text-text-tertiary truncate max-w-lg mt-0.5">{item.question}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {item.source_type === 'auto_flagged' && (
              <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-xs rounded">Auto-Flagged</span>
            )}
            {item.source_type === 'evidence_failed' && (
              <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs rounded">Evidence Failed</span>
            )}
            <CaretDown size={16} className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </button>
        
        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 space-y-4 border-t border-border/50">
                {/* Audit Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-text-tertiary uppercase mb-1">Domain</div>
                    <div className="text-text-secondary">{item.domain}</div>
                  </div>
                  <div>
                    <div className="text-xs text-text-tertiary uppercase mb-1">Regulatory Reference</div>
                    <div className="text-text-secondary">{item.regulatory_reference || 'N/A'}</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-text-tertiary uppercase mb-1">Audit Objective</div>
                  <div className="text-sm text-text-secondary">{item.audit_objective || 'N/A'}</div>
                </div>
                
                <div>
                  <div className="text-xs text-text-tertiary uppercase mb-1">Expected Outcome</div>
                  <div className="text-sm text-gold">{item.expected_outcome || 'N/A'}</div>
                </div>
                
                {/* Evidence Section */}
                <div className="pt-3 border-t border-border/50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-medium text-text-secondary uppercase flex items-center gap-2">
                      <FileText size={14} /> Evidence Files
                    </h4>
                    {canUploadEvidence && (
                      <label className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400 hover:bg-purple-500/20 cursor-pointer transition-colors">
                        {uploading ? (
                          <>
                            <div className="w-3 h-3 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={12} />
                            Upload Evidence
                          </>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleFileUpload}
                          disabled={uploading}
                          accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.csv,.png,.jpg,.jpeg"
                        />
                      </label>
                    )}
                  </div>
                  
                  {item.evidence_files?.length > 0 ? (
                    <div className="space-y-2">
                      {item.evidence_files.map((ev) => {
                        const naviResult = ev.navi_analysis?.success ? ev.navi_analysis.assessment?.assessment_result : null;
                        const resultStyle = naviResult ? NAVI_RESULT_STYLES[naviResult] : null;
                        
                        return (
                          <div key={ev.id} className="bg-background rounded-lg border border-border p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileText size={16} className="text-purple-400" />
                                <span className="text-sm text-text-primary">{ev.filename}</span>
                                <span className="text-xs text-text-tertiary">
                                  {(ev.file_size / 1024).toFixed(1)} KB
                                </span>
                              </div>
                              {!ev.navi_analysis && (
                                <button
                                  onClick={() => handleAnalyze(ev.id)}
                                  disabled={analyzing}
                                  className="flex items-center gap-1 px-2 py-1 bg-gold/10 text-gold text-xs rounded hover:bg-gold/20 transition-colors disabled:opacity-50"
                                >
                                  {analyzing ? (
                                    <>
                                      <div className="w-3 h-3 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                                      Analyzing...
                                    </>
                                  ) : (
                                    <>
                                      <Robot size={12} />
                                      Analyze with NAVI
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                            
                            {/* NAVI Analysis Result */}
                            {resultStyle && (
                              <div className={`mt-2 p-2 rounded ${resultStyle.bg}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span>{resultStyle.icon}</span>
                                  <span className={`text-sm font-medium ${resultStyle.text}`}>{resultStyle.label}</span>
                                </div>
                                {ev.navi_analysis?.assessment?.reasoning && (
                                  <p className="text-xs text-text-tertiary mt-1">{ev.navi_analysis.assessment.reasoning}</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-xs text-text-tertiary">
                      <Upload size={20} className="mx-auto mb-2 opacity-50" />
                      No evidence uploaded yet
                    </div>
                  )}
                </div>
                
                {/* Additional Evidence Request Info */}
                {item.additional_evidence_requested && item.additional_evidence_request_notes && (
                  <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                    <div className="text-xs font-medium text-amber-400 uppercase mb-1">Additional Evidence Requested</div>
                    <p className="text-sm text-text-secondary">{item.additional_evidence_request_notes}</p>
                    <div className="text-xs text-text-tertiary mt-1">
                      Requested on: {new Date(item.additional_evidence_request_date).toLocaleString()}
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                {!isTerminal && (
                  <div className="pt-3 border-t border-border/50 flex flex-wrap gap-2">
                    {canMoveToReview && (
                      <button
                        onClick={handleMoveToReview}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm rounded hover:bg-purple-500/20 transition-colors"
                      >
                        <Eye size={14} />
                        Move to Review
                      </button>
                    )}
                    
                    {canMakeDecision && (
                      <>
                        <button
                          onClick={() => setShowRequestModal(true)}
                          className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm rounded hover:bg-amber-500/20 transition-colors"
                        >
                          <ArrowClockwise size={14} />
                          Request Evidence
                        </button>
                        <button
                          onClick={handleAccept}
                          className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm rounded hover:bg-emerald-500/20 transition-colors"
                        >
                          <CheckCircle size={14} />
                          Accept
                        </button>
                        <button
                          onClick={() => setShowRejectModal(true)}
                          className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded hover:bg-red-500/20 transition-colors"
                        >
                          <XCircle size={14} />
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                )}
                
                {/* Terminal State Info */}
                {item.status === 'accepted' && (
                  <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                      <CheckCircle size={16} />
                      Accepted
                    </div>
                    {item.auditor_decision_notes && (
                      <p className="text-xs text-text-secondary mt-1">{item.auditor_decision_notes}</p>
                    )}
                    <div className="text-xs text-text-tertiary mt-1">
                      {item.auditor_email} • {new Date(item.accepted_at).toLocaleString()}
                    </div>
                  </div>
                )}
                
                {item.status === 'rejected' && (
                  <div className="p-3 bg-gray-500/5 border border-gray-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                      <ArrowRight size={16} />
                      Rejected - Moved to Remediation Tracker
                    </div>
                    {item.auditor_decision_notes && (
                      <p className="text-xs text-text-secondary mt-1">{item.auditor_decision_notes}</p>
                    )}
                    {item.remediation_item_id && (
                      <Link
                        to="/remediation-tracker"
                        className="inline-flex items-center gap-1 text-xs text-gold hover:text-gold-hover mt-2"
                      >
                        <LinkIcon size={12} />
                        View in Remediation Tracker
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      {/* Request Evidence Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background-surface border border-border rounded-lg p-5 w-full max-w-md">
            <h3 className="text-lg font-medium text-text-primary mb-4">Request Additional Evidence</h3>
            <p className="text-sm text-text-secondary mb-4">
              Describe what additional evidence is needed from the user.
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Please provide the following evidence..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none resize-none mb-4"
              rows={4}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowRequestModal(false); setNotes(''); }}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestEvidence}
                className="px-4 py-2 bg-amber-500 text-white text-sm rounded hover:bg-amber-600 transition-colors"
              >
                Request Evidence
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background-surface border border-border rounded-lg p-5 w-full max-w-md">
            <h3 className="text-lg font-medium text-text-primary mb-4">Reject and Move to Remediation</h3>
            <p className="text-sm text-text-secondary mb-4">
              This item will be moved to the Remediation Tracker. Please provide rejection notes.
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none resize-none mb-4"
              rows={4}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowRejectModal(false); setNotes(''); }}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Main Component
const RiskLogPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(searchParams.get('theme') || 'all');
  const [filters, setFilters] = useState({
    status: '',
    source_type: '',
    domain: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchData = useCallback(async () => {
    try {
      const params = { ...filters };
      if (selectedTheme !== 'all') {
        params.regulatory_theme = selectedTheme;
      }
      
      const [dashboardRes, itemsRes] = await Promise.all([
        axios.get(`${API}/risk-log/dashboard`, { params: selectedTheme !== 'all' ? { regulatory_theme: selectedTheme } : {} }),
        axios.get(`${API}/risk-log/items`, { params })
      ]);
      
      setStats(dashboardRes.data);
      setItems(itemsRes.data.items || []);
    } catch (error) {
      console.error('Error fetching risk log data:', error);
      toast.error('Failed to load risk log');
    } finally {
      setLoading(false);
    }
  }, [filters, selectedTheme]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleAction = (action) => {
    if (action === 'refresh') {
      fetchData();
    }
  };
  
  const handleExpand = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };
  
  const filteredItems = items.filter(item => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!item.question_id?.toLowerCase().includes(query) && 
          !item.question?.toLowerCase().includes(query) &&
          !item.domain?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            <span className="text-sm text-text-tertiary">Loading Risk Log...</span>
          </div>
        </div>
      </div>
    );
  }
  
  const pendingActions = stats?.pending_actions || {};
  
  return (
    <div className="flex min-h-screen bg-background" data-testid="risk-log-page">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Breadcrumb */}
        <div className="px-6 pt-4">
          <PageBreadcrumb items={[
            { label: 'Risk Log' }
          ]} />
        </div>

        {/* Header */}
        <div className="px-6 pb-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1">
                Risk Log
              </h1>
              <p className="text-sm text-text-secondary">
                Items flagged for audit review • Auto-flagged (score &lt; 2) and evidence-failed items
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/diagnostics"
                className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-gold transition-colors"
                data-testid="diagnostics-link"
              >
                <ClipboardText size={18} />
                Diagnostics
              </Link>
              <Link
                to="/remediation-tracker"
                className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all"
                data-testid="remediation-tracker-link"
              >
                <ListChecks size={18} />
                Remediation Tracker
              </Link>
            </div>
          </div>
        </div>
        
        {/* Theme Filter Tabs */}
        <div className="p-4 border-b border-border bg-background-surface/50">
          <div className="flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedTheme('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                selectedTheme === 'all' 
                  ? 'bg-gold text-gold-foreground' 
                  : 'bg-background border border-border text-text-secondary hover:border-gold/50'
              }`}
            >
              All Themes
            </button>
            {Object.entries(REGULATORY_THEMES).map(([key, theme]) => {
              const ThemeIcon = theme.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedTheme(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                    selectedTheme === key 
                      ? 'text-white' 
                      : 'bg-background border border-border text-text-secondary hover:border-gold/50'
                  }`}
                  style={selectedTheme === key ? { backgroundColor: theme.color } : {}}
                >
                  <ThemeIcon size={14} />
                  {theme.name}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Stats */}
        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-6 gap-4">
            <StatCard
              label="Total Items"
              value={stats?.stats?.total || 0}
              icon={Warning}
              color="gold"
            />
            <StatCard
              label="Auto-Flagged"
              value={stats?.stats?.auto_flagged || 0}
              icon={Warning}
              color="red-400"
              subtext="Score < 2"
            />
            <StatCard
              label="Evidence Failed"
              value={stats?.stats?.evidence_failed || 0}
              icon={XCircle}
              color="amber-400"
            />
            <StatCard
              label="Pending Review"
              value={stats?.stats?.pending_review || 0}
              icon={Eye}
              color="purple-400"
            />
            <StatCard
              label="Accepted"
              value={stats?.stats?.accepted || 0}
              icon={CheckCircle}
              color="emerald-400"
            />
            <StatCard
              label="Rejected"
              value={stats?.stats?.rejected || 0}
              icon={ArrowRight}
              color="gray-400"
              subtext="In Remediation"
            />
          </div>
          
          {/* Action Required Banner */}
          {pendingActions.total_action_required > 0 && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Warning size={20} className="text-amber-400" />
                <span className="text-sm text-amber-400">
                  <strong>{pendingActions.total_action_required}</strong> items require attention
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-text-tertiary">
                {pendingActions.pending_review > 0 && (
                  <span>{pendingActions.pending_review} pending review</span>
                )}
                {pendingActions.additional_evidence_requested > 0 && (
                  <span>{pendingActions.additional_evidence_requested} awaiting evidence</span>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Filters */}
        <div className="p-4 border-b border-border bg-background-surface/50">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search by ID, question, or domain..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="auto_flagged">Auto-Flagged</option>
              <option value="evidence_required">Evidence Required</option>
              <option value="pending_validation">Pending Validation</option>
              <option value="evidence_failed">Evidence Failed</option>
              <option value="pending_review">Pending Review</option>
              <option value="additional_evidence_requested">Additional Evidence Requested</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <select
              value={filters.source_type}
              onChange={(e) => setFilters(prev => ({ ...prev, source_type: e.target.value }))}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="">All Sources</option>
              <option value="auto_flagged">Auto-Flagged (Score &lt; 2)</option>
              <option value="evidence_failed">Evidence Failed</option>
            </select>
          </div>
        </div>
        
        {/* Items List */}
        <div className="p-6">
          {filteredItems.length > 0 ? (
            <div className="space-y-3">
              {filteredItems.map((item, index) => (
                <RiskLogItemCard
                  key={item.id}
                  item={item}
                  onAction={handleAction}
                  onExpand={handleExpand}
                  isExpanded={expandedItem === item.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Shield size={48} className="mx-auto mb-4 text-text-tertiary/30" />
              <h3 className="text-lg font-medium text-text-secondary mb-2">No Risk Log Items</h3>
              <p className="text-sm text-text-tertiary max-w-md mx-auto">
                Items with maturity score &lt; 2 or failed evidence validation will appear here.
                Complete a Cyber Diagnostics assessment to generate risk items.
              </p>
              <Link
                to="/cyber-diagnostics"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all"
              >
                <ClipboardText size={18} />
                Start Assessment
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskLogPage;
